import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Heading2, List, ListOrdered, Link2 } from "lucide-react";

export default function RichTextEditor({ value, onChange }) {
    const ref = useRef(null);

    // Sync external value changes (e.g. loading a ready-made draft) without clobbering typing
    useEffect(() => {
        const el = ref.current;
        if (!el || document.activeElement === el) return;
        if ((value || "") !== el.innerHTML) el.innerHTML = DOMPurify.sanitize(value || "");
    }, [value]);

    const exec = (cmd, arg = null) => {
        document.execCommand(cmd, false, arg);
        ref.current?.focus();
        onChange(ref.current?.innerHTML || "");
    };

    const addLink = () => {
        const url = window.prompt("Link URL");
        if (url) exec("createLink", url);
    };

    const Btn = ({ icon: Icon, onClick, label }) => (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            data-testid={`editor-${label}`}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-300/80 transition-colors hover:bg-blue-500/15 hover:text-slate-400"
        >
            <Icon className="h-4 w-4" />
        </button>
    );

    return (
        <div className="overflow-hidden rounded-xl border border-blue-500/20 bg-[#070e1c]">
            <div className="flex flex-wrap gap-1 border-b border-blue-500/15 bg-[#0c172e] p-1.5">
                <Btn icon={Bold} label="bold" onClick={() => exec("bold")} />
                <Btn icon={Italic} label="italic" onClick={() => exec("italic")} />
                <Btn icon={Underline} label="underline" onClick={() => exec("underline")} />
                <Btn icon={Heading2} label="heading" onClick={() => exec("formatBlock", "<h2>")} />
                <Btn icon={List} label="bullets" onClick={() => exec("insertUnorderedList")} />
                <Btn icon={ListOrdered} label="numbers" onClick={() => exec("insertOrderedList")} />
                <Btn icon={Link2} label="link" onClick={addLink} />
            </div>
            <div
                ref={ref}
                data-testid="blog-body-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={() => onChange(ref.current?.innerHTML || "")}
                className="prose-editor min-h-[220px] px-4 py-3 text-sm leading-relaxed text-blue-50 outline-none"
            />
        </div>
    );
}
